// composables/useSessionAuth.js
import axios from '@/services/axios'
import { ref } from 'vue'

// Global reactive state for user data
const userData = ref(null)
const userAbilities = ref([])
const stores = ref([])
const selectedStore = ref(null)
const isLoading = ref(false)

export function useSessionAuth() {
  
  /**
   * Fetch user data from session
   */
  const fetchUserData = async () => {
    try {
      isLoading.value = true

      const response = await axios.get('/api/auth/me', {
        withCredentials: true,
        timeout: 10000,
      })

      if (response.status === 200 && response.data) {
        const { userData: user, userAbilities: abilities, stores: userStores, selectedStore: selected } = response.data

        // Update reactive state (include avatar if provided)
        userData.value = {
          id: user.id,
          email: user.email,
          firstname: user.firstname,
          lastname: user.lastname,
          username: user.username,
          phone: user.phone,
          avatar: user.avatar || null,
          role: {
            id: user.role_id,
            // The server sends this already resolved from role_code -- see
            // User::roleLabel(). The fallback here was `role_id === 1`,
            // which is `org_owner` in this module, so platform_super_admin
            // (id 4) was labelled 'user' every time role.name was absent --
            // which is always, because the API sends `role` as a string.
            name: user.role?.name || user.role || 'user',
          },
          'role_id': user.role_id,

          // MUST be carried — the router guard compares this against
          // check-session and reloads on any mismatch; omitting it (this
          // rebuild used a fixed field list) wiped the flag on every /me
          // fetch and drove an endless reload loop (2026-08-13).
          'can_manage_users': Boolean(user.can_manage_users),
          StoreID: user.StoreID,
          StoreName: user.StoreName,
          StoreSelected: user.StoreSelected,
          TabernaAccountID: user.TabernaAccountID,

          // Server feature flags (e.g. features.billing = Stripe purchasing
          // enabled). Read synchronously by the nav builder from localStorage.
          features: response.data.features || {},
        }
        
        // Persist to localStorage for components still reading from it
        try {
          localStorage.setItem('userData', JSON.stringify(userData.value))
        } catch (e) {
          console.warn('Failed to persist userData to localStorage:', e)
        }

        // Dispatch event so existing listeners (UserProfile) update immediately
        window.dispatchEvent(new CustomEvent('user-profile-updated'))
        
        userAbilities.value = abilities || []
        stores.value = userStores || []
        selectedStore.value = selected

        return true
      }
    } catch (error) {
      console.log('Failed to fetch user data:', error.response?.status, error.message)
      
      // If we get a 401, the user is not authenticated
      if (error.response?.status === 401) {
        clearAuth()
        
        return false
      }
      
      return false
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Check if user is authenticated
   */
  const isAuthenticated = async () => {
    // If we already have user data, consider authenticated
    if (userData.value) {
      return true
    }

    // Otherwise, try to fetch from session
    return await fetchUserData()
  }

  /**
   * Clear authentication data
   */
  const clearAuth = () => {
    userData.value = null
    userAbilities.value = []
    stores.value = []
    selectedStore.value = null

    // Also clear localStorage to prevent stale data
    try {
      localStorage.removeItem('userData')
      localStorage.removeItem('userAbilities')
      localStorage.removeItem('accessToken')

      // Notify components that user data has been cleared
      window.dispatchEvent(new CustomEvent('user-profile-updated'))
    } catch (e) {
      console.warn('Failed to clear localStorage:', e)
    }
  }

  /**
   * Get current user data
   */
  const getUser = () => userData.value

  /**
   * Get user abilities/permissions
   */
  const getAbilities = () => userAbilities.value

  /**
   * Get user stores
   */
  const getStores = () => stores.value

  /**
   * Get selected store
   */
  const getSelectedStore = () => selectedStore.value

  return {
    // Methods
    fetchUserData,
    isAuthenticated,
    clearAuth,
    
    // Getters
    getUser,
    getAbilities,
    getStores,
    getSelectedStore,
    
    // Reactive state (for components that need reactivity)
    userData,
    userAbilities,
    stores,
    selectedStore,
    isLoading,
  }
}
